/**
 * HTTP Implementation of Public Scan Gateway
 *
 * Handles fetching NFC scan context via REST API.
 * No authentication required (public endpoint).
 *
 * Backend endpoint: GET /api/v1/viewer/nfc/context?url={full-nfc-url}
 * NFC URL format: {nfcBaseUrl}/nfc/{nfcId}/variant/{signature}
 *
 * IMPORTANT: nfcBaseUrl must match backend NFC_EXPECTED_DOMAIN
 * - Backend validates NFC URLs against this domain
 * - Use production domain (https://app.anyw3ar.com) even in development
 * - This is NOT the frontend URL (which may be localhost:5173)
 */

import type { IPublicScanGateway } from "@/domain/ports/IPublicScanGateway";
import type { ViewerNfcScanContextResponse } from "@/domain/types/ViewerNfcScanContext";

export class HttpPublicScanGateway implements IPublicScanGateway {
  private apiBaseUrl: string;
  private nfcBaseUrl: string;

  constructor(apiBaseUrl: string, nfcBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.nfcBaseUrl = nfcBaseUrl;
  }

  /**
   * Get scan context for an NFC item
   *
   * @param productId - The product identifier from the NFC URL
   * @param itemId - The item identifier from the NFC URL
   * @param signature - The cryptographic signature for authenticity verification
   * @returns Response indicating the item's state (invalid, stolen, claimed, or unclaimed)
   * @throws Error if the network request fails or returns an error response
   */
  async getScanContext(
    productId: string,
    itemId: string,
    signature: string
  ): Promise<ViewerNfcScanContextResponse> {
    try {
      // Reconstruct the full NFC URL with backend-expected domain
      // Format: {nfcBaseUrl}/nfc/{productId}/{itemId}/{signature}
      // IMPORTANT: Uses production domain even in dev (must match backend NFC_EXPECTED_DOMAIN)
      const nfcUrl = `${this.nfcBaseUrl}/nfc/${productId}/${itemId}/${signature}`;

      // Build query string with full NFC URL
      const params = new URLSearchParams({
        url: nfcUrl,
      });

      const apiUrl = `${this.apiBaseUrl}/api/v1/viewer/nfc/context?${params.toString()}`;

      console.log("[HttpPublicScanGateway] Fetching scan context:", {
        nfcUrl,
        apiUrl,
      });

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("[HttpPublicScanGateway] Response status:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: "Unknown error" }));

        console.error("[HttpPublicScanGateway] Request failed:", {
          status: response.status,
          error,
        });

        // Handle specific HTTP error codes
        if (response.status === 400) {
          // Bad request - likely invalid parameters
          const errorMessage =
            error.error || error.message || "Invalid request parameters";
          throw new Error(errorMessage);
        }

        if (response.status === 403) {
          // Invalid signature - fraudulent/counterfeit item
          const errorMessage =
            error.message || "Invalid signature - fraudulent item detected";
          const fraudulentError = new Error(errorMessage);
          // Add custom property to distinguish fraudulent errors
          (fraudulentError as any).isFraudulent = true;
          (fraudulentError as any).statusCode = 403;
          throw fraudulentError;
        }

        if (response.status === 404) {
          // NFC ID not found
          throw new Error("NFC item not found");
        }

        if (response.status === 500) {
          // Server error
          throw new Error("Server error occurred");
        }

        // Generic error handling
        const errorMessage =
          error.error || error.message || `HTTP error ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();

      console.log("[HttpPublicScanGateway] Scan context fetched successfully:", {
        isAuthentic: data.isAuthentic,
        type: data.type || "invalid",
        hasProductInfo: "productInfo" in data,
      });

      return data as ViewerNfcScanContextResponse;
    } catch (error) {
      console.error("[HttpPublicScanGateway] Failed to fetch scan context:", error);

      // Re-throw with more context if it's a network error
      if (error instanceof Error) {
        if (error.message === "Failed to fetch" || error.message.includes("network")) {
          throw new Error("NetworkError");
        }
        throw error;
      }

      throw new Error("UnknownError");
    }
  }
}
